import { Box, Divider, List, ListItem, ListItemText, Paper, TextField } from '@mui/material';
import Label from 'components/@home/drawers/AppDrawer/Form/Label';
import StepPdfSource from 'components/@home/@documents/SentDocuments/edit-steps/StepPdfSource';
import DeliveryAuditReportField from 'components/@home/@documents/SentDocuments/components/DeliveryAuditReportField';
import { getPdfUploadStepKey } from 'components/@home/@documents/SentDocuments/edit-steps/editTemplateFlow';
import { bool, func, object, string } from 'prop-types';
import React from 'react';
import { I18n } from 'utils/i18n';

const classes = {
  field: {
    minWidth: 200,
    flex: '1 1 auto',
  },
  fieldLabel: {
    mt: 0,
  },
};

const handleSingleLineEnterKeyDown = (e, submitEnabled, onEnterSubmit) => {
  if (e.key !== 'Enter' || e.shiftKey) return;
  if (e.nativeEvent?.isComposing) return;
  e.preventDefault();
  if (!submitEnabled) return;
  onEnterSubmit();
};

const StepReview = ({
  template,
  setTemplate,
  uploadBusy = false,
  onPdfDrop,
  canvasPdfSrc = null,
  onReplacePdf,
  submitEnabled,
  onEnterSubmit,
}) => {
  const summaryRows = [
    {
      label: I18n.t('HrBot.EditTemplate.DocumentSource.label'),
      value:
        template.documentSource === 'fixed'
          ? I18n.t('HrBot.EditTemplate.DocumentSource.fixed')
          : I18n.t('HrBot.EditTemplate.DocumentSource.perSend'),
    },
  ];

  if (template.isDigitalSignature) {
    summaryRows.push({
      label: I18n.t('HrBot.EditTemplate.SigningMode.label'),
      value: I18n.t(`HrBot.EditTemplate.SigningMode.${template.signingMode || 'onePerMember'}`),
    });
  }

  const showTemplatePdf = getPdfUploadStepKey(template) === 'review';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={classes.field}>
        <Label sx={classes.fieldLabel}>{I18n.t('HrBot.EditTemplate.Name')}</Label>
        <TextField
          fullWidth
          size="small"
          value={template.name || ''}
          autoFocus
          onChange={e => setTemplate(t => ({ ...t, name: e.target.value }))}
          onKeyDown={e => handleSingleLineEnterKeyDown(e, submitEnabled, onEnterSubmit)}
        />
      </Box>

      <DeliveryAuditReportField
        sendReportTo={template.sendReportTo}
        onChange={v => setTemplate(t => ({ ...t, sendReportTo: v }))}
      />

      {showTemplatePdf && (
        <>
          <StepPdfSource
            template={template}
            uploadBusy={uploadBusy}
            onPdfDrop={onPdfDrop}
            canvasPdfSrc={canvasPdfSrc}
            onReplacePdf={onReplacePdf}
          />
          <Divider />
        </>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <List disablePadding>
          {summaryRows.map((row, index) => (
            <React.Fragment key={row.label}>
              <ListItem sx={{ py: 1.25, px: 2 }}>
                <ListItemText
                  secondary={row.value}
                  primary={row.label}
                  secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 600 } }}
                />
              </ListItem>
              {index < summaryRows.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {(template.split === true || (template.items || []).length > 0) && (
        <Box sx={classes.field}>
          <Label sx={classes.fieldLabel}>{I18n.t('HrBot.EditTemplate.Prefix')}</Label>
          <TextField
            fullWidth
            size="small"
            value={template.prefix}
            onChange={e => setTemplate(t => ({ ...t, prefix: e.target.value }))}
            onKeyDown={e => handleSingleLineEnterKeyDown(e, submitEnabled, onEnterSubmit)}
            sx={{ mb: 2 }}
          />
        </Box>
      )}

      <Box sx={classes.field}>
        <Label sx={classes.fieldLabel}>{I18n.t('HrBot.Steps.Message')}</Label>
        <TextField
          fullWidth
          multiline
          rows={6}
          value={template.defaultMessage}
          onChange={e => setTemplate(t => ({ ...t, defaultMessage: e.target.value }))}
        />
      </Box>
    </Box>
  );
};

StepReview.propTypes = {
  template: object.isRequired,
  setTemplate: func.isRequired,
  uploadBusy: bool,
  onPdfDrop: func.isRequired,
  canvasPdfSrc: string,
  onReplacePdf: func.isRequired,
  submitEnabled: bool.isRequired,
  onEnterSubmit: func.isRequired,
};

export default StepReview;
